// Акции на витрине (П3): «Горящие акции», таймер и цена акции на странице
// товара, «Календарь акций» и подписка на начало акции. Схемы, http и мок
// живут здесь, а не в общих types.ts/http.ts: в `Api` это `api.promos.*`.
//
// Цена со скидкой здесь — только для показа. Корзину и заказ считает шлюз
// (пакет П2, поле `activePromo` товара); пока его нет, витрина показывает
// скидку акции по данным этого модуля.
import { z } from "zod";

import { Product } from "./types";

export const PromoDiscountType = z.enum(["percent", "amount"]);

export const HotPromoInfo = z.object({
	id: z.string(),
	title: z.string(),
	discountType: PromoDiscountType,
	discountValue: z.number(),
	startsAt: z.string(),
	endsAt: z.string(),
	shopId: z.string(),
	price: z.number(),
	basePrice: z.number(),
});
export type HotPromoInfo = z.infer<typeof HotPromoInfo>;

export const HotPromoItem = z.object({ product: Product, promo: HotPromoInfo });
export type HotPromoItem = z.infer<typeof HotPromoItem>;

export const ProductPromo = HotPromoInfo.extend({ kind: z.enum(["regular", "flash", "collaboration"]) });
export type ProductPromo = z.infer<typeof ProductPromo>;

export const CalendarPromo = z.object({
	id: z.string(),
	source: z.enum(["shop", "platform", "code"]),
	kind: z.enum(["regular", "flash", "collaboration", "platform", "code"]),
	title: z.string(),
	description: z.string(),
	discountType: PromoDiscountType,
	discountValue: z.number(),
	startsAt: z.string().nullable(),
	endsAt: z.string().nullable(),
	state: z.enum(["active", "scheduled"]),
	shops: z.array(z.object({ id: z.string(), name: z.string(), slug: z.string().nullable() })),
	categories: z.array(z.object({ id: z.string(), name: z.string() })),
	productCount: z.number(),
	products: z.array(
		z.object({
			id: z.string(),
			name: z.string(),
			photo: z.string().nullable(),
			price: z.number(),
			promoPrice: z.number().nullable(),
		}),
	),
	code: z.string().nullable(),
	subscribed: z.boolean(),
});
export type CalendarPromo = z.infer<typeof CalendarPromo>;

export const PromoCalendar = z.object({
	from: z.string(),
	to: z.string(),
	items: z.array(CalendarPromo),
	groups: z.array(z.object({ categoryId: z.string().nullable(), categoryName: z.string(), promoIds: z.array(z.string()) })),
	categories: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type PromoCalendar = z.infer<typeof PromoCalendar>;

export type PromoCalendarQuery = { from?: string; to?: string; categoryId?: string; kind?: string };

export type PromosApi = {
	calendar(query?: PromoCalendarQuery): Promise<PromoCalendar>;
	hot(limit?: number): Promise<HotPromoItem[]>;
	/** Действующая акция товара (таймер и цена) или null. */
	productPromo(productId: string): Promise<ProductPromo | null>;
	subscribe(id: string): Promise<{ subscribed: boolean }>;
	unsubscribe(id: string): Promise<{ subscribed: boolean }>;
};

type Requester = <T>(path: string, opts?: { method?: string; body?: unknown }) => Promise<T>;

const enc = encodeURIComponent;

/** Элементы списка — поштучно: один кривой не уносит весь блок. */
const parseEach = <T>(schema: z.ZodType<T>, raw: unknown): T[] =>
	Array.isArray(raw)
		? raw.flatMap((entry) => {
				const parsed = schema.safeParse(entry);
				return parsed.success ? [parsed.data] : [];
			})
		: [];

export const createPromosHttp = (request: Requester): PromosApi => ({
	calendar: async (query = {}) => {
		const p = new URLSearchParams();
		if (query.from) p.set("from", query.from);
		if (query.to) p.set("to", query.to);
		if (query.categoryId) p.set("categoryId", query.categoryId);
		if (query.kind) p.set("kind", query.kind);
		const qs = p.toString();
		const raw = await request<{ items?: unknown[] } & Record<string, unknown>>(`/promos/calendar${qs ? `?${qs}` : ""}`);
		return PromoCalendar.parse({ ...raw, items: parseEach(CalendarPromo, raw?.items) });
	},
	hot: async (limit = 24) => parseEach(HotPromoItem, await request(`/promos/hot?limit=${limit}`)),
	productPromo: async (productId) => {
		const raw = await request<unknown>(`/promos/product/${enc(productId)}`);
		return raw == null ? null : ProductPromo.parse(raw);
	},
	subscribe: (id) => request(`/promos/${enc(id)}/subscribe`, { method: "POST" }),
	unsubscribe: (id) => request(`/promos/${enc(id)}/subscribe`, { method: "DELETE" }),
});

// ── Мок: витрина без шлюза ──────────────────────────────────────────────────
const hours = (n: number) => new Date(Date.now() + n * 3_600_000).toISOString();
const subscribed = new Set<string>();

export const createPromosMock = (products: () => Product[]): PromosApi => {
	const pool = () => products().filter((p) => p.photos.length > 0);
	const flashEnds = hours(5);
	const flashStarts = hours(-2);
	const hotItems = (): HotPromoItem[] =>
		pool()
			.slice(0, 6)
			.map((product) => ({
				product,
				promo: {
					id: "spr-mock-flash",
					title: "Минус 30 % до вечера",
					discountType: "percent" as const,
					discountValue: 30,
					startsAt: flashStarts,
					endsAt: flashEnds,
					shopId: product.shopId,
					price: Math.round(product.price * 0.7 * 100) / 100,
					basePrice: product.price,
				},
			}));
	const calendarItems = (): CalendarPromo[] => {
		const sample = pool();
		// Категория — прямо из товара мока: своих id категорий здесь не держим.
		const categoryOf = (p: Product | undefined) => (p ? [{ id: p.categoryId, name: p.category }] : []);
		const preview = (from: number, discount: number) =>
			sample.slice(from, from + 4).map((p) => ({
				id: p.id,
				name: p.name,
				photo: p.photos[0] ?? null,
				price: p.price,
				promoPrice: Math.round(p.price * (1 - discount / 100) * 100) / 100,
			}));
		return [
			{
				id: "spr-mock-flash",
				source: "shop",
				kind: "flash",
				title: "Минус 30 % до вечера",
				description: "Горящая акция мастерской",
				discountType: "percent",
				discountValue: 30,
				startsAt: flashStarts,
				endsAt: flashEnds,
				state: "active",
				shops: [{ id: "shop-handmade", name: "Мастерская Анны", slug: "handmade" }],
				categories: categoryOf(sample[0]),
				productCount: 6,
				products: preview(0, 30),
				code: null,
				subscribed: false,
			},
			{
				id: "spr-mock-autumn",
				source: "shop",
				kind: "regular",
				title: "Осенние скидки",
				description: "Минус 20 % на платья и верхнюю одежду",
				discountType: "percent",
				discountValue: 20,
				startsAt: hours(48),
				endsAt: hours(24 * 9),
				state: "scheduled",
				shops: [{ id: "shop-silver-moon", name: "Silver Moon", slug: "silver-moon" }],
				categories: categoryOf(sample[4]),
				productCount: 4,
				products: preview(4, 20),
				code: null,
				subscribed: subscribed.has("spr-mock-autumn"),
			},
			{
				id: "ppc-mock-black-friday",
				source: "platform",
				kind: "platform",
				title: "Чёрная пятница OSMIX",
				description: "Большая распродажа площадки",
				discountType: "percent",
				discountValue: 25,
				startsAt: hours(24 * 12),
				endsAt: hours(24 * 15),
				state: "scheduled",
				shops: [],
				categories: [],
				productCount: 0,
				products: [],
				code: null,
				subscribed: subscribed.has("ppc-mock-black-friday"),
			},
		];
	};
	return {
		calendar: async (query = {}) => {
			let items = calendarItems();
			if (query.kind) items = items.filter((item) => item.kind === query.kind);
			if (query.categoryId) items = items.filter((item) => item.categories.some((c) => c.id === query.categoryId));
			const groups = new Map<string, { categoryId: string | null; categoryName: string; promoIds: string[] }>();
			for (const item of items) {
				const cats = item.categories.length ? item.categories : [null];
				for (const cat of cats) {
					const key = cat?.id ?? "";
					const group = groups.get(key) ?? { categoryId: cat?.id ?? null, categoryName: cat?.name ?? "Для всех товаров", promoIds: [] };
					group.promoIds.push(item.id);
					groups.set(key, group);
				}
			}
			return {
				from: new Date().toISOString(),
				to: hours(24 * 30),
				items,
				groups: [...groups.values()],
				categories: calendarItems().flatMap((item) => item.categories),
			};
		},
		hot: async () => hotItems(),
		productPromo: async (productId) => {
			const found = hotItems().find((item) => item.product.id === productId);
			return found ? { ...found.promo, kind: "flash" as const } : null;
		},
		subscribe: async (id) => {
			subscribed.add(id);
			return { subscribed: true };
		},
		unsubscribe: async (id) => {
			subscribed.delete(id);
			return { subscribed: false };
		},
	};
};
